import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ArrowRight } from "@phosphor-icons/react";

const EMIRATES = ["All", "Dubai", "Abu Dhabi", "Sharjah", "Ras Al Khaimah", "Ajman", "Fujairah", "Umm Al Quwain"];

export default function Developers() {
  const [items, setItems] = useState([]);
  const [emirate, setEmirate] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/developers").then((r) => setItems(r.data.items || [])); }, []);

  const filtered = useMemo(() => {
    return items.filter((d) => {
      const inEmirate = emirate === "All" || d.emirate === emirate || (d.emirates_of_operation || []).includes(emirate);
      const inSearch = !q || (d.name || "").toLowerCase().includes(q.toLowerCase());
      return inEmirate && inSearch;
    });
  }, [items, emirate, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((d) => {
      const key = d.emirate || "Other";
      (g[key] = g[key] || []).push(d);
    });
    return g;
  }, [filtered]);

  return (
    <div data-testid="developers-page">
      <SEO
        title="UAE developers — Emaar, Damac, Aldar, Nakheel and 60+ more"
        description="Directory of every major UAE property developer we work with — across Dubai, Abu Dhabi, Sharjah, Ras Al Khaimah, Ajman, Fujairah and Umm Al Quwain."
        path="/developers"
      />
      <section className="pt-6 pb-10 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Developers", path: "/developers" }]} />
          <div className="overline text-[#001588] mb-4">Developers</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight max-w-4xl">
            The names<br/><span className="italic text-neutral-600">shaping the Emirates.</span>
          </h1>
          <p className="text-neutral-600 mt-6 max-w-2xl">
            {items.length}+ master and boutique developers across the seven emirates. Filter by emirate or search by name.
          </p>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-white sticky top-16 z-30">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex flex-wrap items-center gap-3">
          {EMIRATES.map((e) => (
            <button
              key={e}
              data-testid={`dev-emirate-${e.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setEmirate(e)}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
                emirate === e ? "bg-[#001588] text-white border-[#001588]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#001588]"
              }`}
            >{e}</button>
          ))}
          <input
            data-testid="dev-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search developer…"
            className="ml-auto text-sm border border-neutral-200 px-3 py-1.5 min-w-[220px] bg-white text-neutral-900 focus:outline-none focus:border-[#001588]"
          />
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 space-y-14">
          {Object.entries(grouped).map(([em, devs]) => (
            <div key={em}>
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-serif text-3xl">{em}</h2>
                <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">{devs.length} developers</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {devs.map((d) => (
                  <Link
                    key={d.slug}
                    to={`/developers/${d.slug}`}
                    data-testid={`dev-card-${d.slug}`}
                    className="group block border border-neutral-200 bg-white hover:border-[#001588] transition-all hover:shadow-md p-5 text-center"
                  >
                    <div className="aspect-square w-24 mx-auto mb-4 flex items-center justify-center bg-white">
                      {d.logo ? (
                        <img src={d.logo} alt={d.name} className="max-w-full max-h-full object-contain" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-[#001588] text-white flex items-center justify-center font-serif text-3xl">
                          {(d.name || "").split(" ").slice(0, 2).map((w) => w[0]).join("")}
                        </div>
                      )}
                    </div>
                    <h3 className="font-serif text-base leading-tight text-neutral-900 group-hover:text-[#001588] transition-colors min-h-[2.5em]">{d.name}</h3>
                    {d.year_established && <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">Est. {d.year_established}</div>}
                    <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] uppercase tracking-[0.2em] text-[#001588] inline-flex items-center gap-1">
                      View <ArrowRight size={11} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-sm text-neutral-500">No developers match your search.</p>}
        </div>
      </section>
    </div>
  );
}
